import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { useConceptData, useCandidate, useHotkeys, useSelection, useVirtual, filterRows, actionsFor, GROUP_LABEL, GROUP_ORDER, STATUS_LABEL, rel, fmt } from "@/concepts/shared";
import { Shell, Palette, Tag, Kbd, Mark, Group, GroupGlyph, Ready, StatusMeter, ModeSwitch, Legend, outcomeText, to } from "@/concepts/clinical/kit";
import { useActionHost, ActionList, eActions, BulkQueue, confirmKind, useApi } from "@/concepts/clinical/forms";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

const reqCount = (c) => (c.age_min != null || c.age_max != null ? 1 : 0) + (c.sex && c.sex !== "any" ? 1 : 0) + c.required_conditions.length
  + (c.required_medications.length ? 1 : 0) + (c.smoking_statuses.length ? 1 : 0) + (c.min_pack_years != null ? 1 : 0);
const KINDS = [["required", "Required"], ["exclusion", "Exclusions"], ["desirable", "Desirable"]];
const OUT_TONE = { supported: "text-[color:var(--met)]", not_met: "text-[color:var(--fail)]", unknown: "text-[color:var(--unk)]" };

const useWB = (initialGroup) => {
  const ctx = useConceptData();
  const [group, setGroup] = useState(initialGroup);
  const [ready, setReady] = useState("all");
  const [q, setQ] = useState("");
  const rows = useMemo(() => filterRows(ctx.wb?.rows || [], { group, q }).filter((r) => ready === "all" || r.readiness === ready), [ctx.wb, group, q, ready]);
  return { ...ctx, group, setGroup, ready, setReady, q, setQ, rows };
};

const evKind = (ci, nReq) => (ci < nReq ? "required" : "exclusion");
const rowEvidence = (r, nReq) => r.evidence.map((e, ci) => ({ ...e, kind: evKind(ci, nReq) }));

const Distribution = ({ rows, group, setGroup, compact }) => {
  const n = rows.length;
  return (
    <div className="flex gap-[3px]" role="tablist" aria-label="Match groups" data-testid="cm-groups">
      {!compact && (
        <button role="tab" aria-selected={group === "all"} onClick={() => setGroup("all")} data-testid="cm-group-all"
          className={`flex w-[104px] shrink-0 flex-col justify-between rounded-[6px] border px-3.5 py-2.5 text-left ${group === "all" ? "border-[color:var(--ink)] bg-[color:var(--ink)] text-[color:var(--bg)]" : "rule-strong hover:border-[color:var(--ink)]"}`}>
          <span className="text-[26px] font-semibold tracking-[-0.04em] tnum leading-none">{n}</span><span className="text-[12.5px] font-medium">All in pool</span>
        </button>
      )}
      {GROUP_ORDER.map((g) => {
        const c = rows.filter((r) => r.group === g).length;
        const on = group === g;
        return (
          <button key={g} role="tab" aria-selected={on} onClick={() => setGroup(g)} data-testid={`cm-group-${g}`} style={{ flexGrow: Math.max(c, n * 0.12), flexBasis: 0 }}
            className={`flex min-w-[92px] flex-col justify-between rounded-[6px] text-left ${compact ? "px-3 py-2" : "px-3.5 py-2.5"} ${on ? "bg-[color:var(--accent-soft)] shadow-[inset_0_0_0_2px_var(--accent)]" : "bg-[color:var(--surface)] hover:shadow-[inset_0_0_0_1px_var(--rule-strong)]"}`}>
            <span className={`font-semibold tracking-[-0.04em] tnum leading-none ${compact ? "text-[20px]" : "text-[26px]"}`}>{c}</span>
            <span className="mt-1.5 flex items-center gap-1.5 truncate text-[12.5px] font-medium"><GroupGlyph group={g} />{GROUP_LABEL[g]}</span>
          </button>
        );
      })}
    </div>
  );
};

const CriteriaStrip = ({ checks }) => (
  <div className="grid grid-cols-[1.4fr_1fr_0.8fr] gap-8 border-y rule py-3.5" data-testid="cm-criteria">
    {KINDS.map(([k, t]) => {
      const list = (checks || []).filter((c) => c.kind === k);
      return (
        <div key={k} className="min-w-0">
          <p className="eyebrow mb-1.5 flex items-center gap-1.5"><Mark outcome="supported" kind={k} label={t} size={12} />{t} · {list.length}</p>
          <p className="line-clamp-2 text-[13.5px] leading-relaxed text-[color:var(--ink-2)]" title={list.map((c) => c.short || c.label).join(" · ")}>{list.length ? list.map((c) => c.short || c.label).join("  ·  ") : "None set"}</p>
        </div>
      );
    })}
  </div>
);

const Restrictions = ({ d }) => {
  const ev = d.evaluation;
  const list = [...ev.restrictions.map((r) => [r.severity === "blocked" ? "fail" : "unk", r.severity === "blocked" ? "Blocks outreach" : "Restricts outreach", r.label]), ...ev.conflicts.map((c) => ["conflict", "Study conflict", c.label])];
  if (!list.length) return <p className="flex items-center gap-2 text-[13.5px] text-[color:var(--met)]" data-testid="cm-no-restrictions"><Mark outcome="supported" label="Contact" size={13} />No contact restrictions or study conflicts</p>;
  return (
    <ul className="space-y-1.5" data-testid="cm-restrictions">
      {list.map(([tone, kind, label]) => (
        <li key={label} className={`grid grid-cols-[132px_1fr] items-start gap-3 rounded-[6px] border-l-[3px] px-3 py-2.5 text-[13.5px] ${tone === "fail" ? "border-[color:var(--fail)] bg-[color:var(--fail-soft)]" : tone === "unk" ? "border-[color:var(--unk)] bg-[color:var(--unk-soft)]" : "border-[color:var(--conflict)] bg-[color:var(--conflict-soft)]"}`}>
          <span className={`text-[12.5px] font-semibold ${tone === "fail" ? "text-[color:var(--fail)]" : tone === "unk" ? "text-[color:var(--unk)]" : "text-[color:var(--conflict)]"}`}>{kind}</span><span>{label}</span>
        </li>
      ))}
    </ul>
  );
};

export const ToConfirm = ({ d, host }) => {
  const list = d.evaluation.checks.filter((c) => c.outcome === "unknown" && c.kind !== "desirable");
  if (!list.length) return null;
  return (
    <div className="rounded-[6px] border border-dashed border-[color:var(--unk)] px-3 py-2.5 text-[13.5px]" data-testid="cm-to-confirm">
      <p className="mb-1 text-[12.5px] font-semibold text-[color:var(--unk)]">Needs information · not counted against the candidate</p>
      {list.map((c) => (
        <div key={c.key + c.kind} className="flex flex-wrap items-center justify-between gap-2 border-b rule py-1.5 last:border-0">
          <span><span className="font-medium">{c.short || c.label}</span> <span className="text-[color:var(--mute)]">· {c.detail}</span></span>
          <span className="flex gap-3 text-[13px] font-medium">
            <button className="text-[color:var(--accent-text)] hover:underline" onClick={() => host.openForm("confirm", confirmKind(c))} data-testid={`cm-confirm-${c.key}`}>Confirm Data</button>
            <button className="text-[color:var(--ink-2)] hover:underline" onClick={() => host.openForm("confirm_task", confirmKind(c))} data-testid={`cm-confirm-task-${c.key}`}>Create Confirm Data Task</button>
          </span>
        </div>
      ))}
    </div>
  );
};

export const Tasks = ({ d }) => {
  const { post, pending } = useApi();
  const open = d.tasks.filter((t) => t.status === "open");
  if (!open.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const done = async (t) => { try { await post(`/tasks/${t.id}/complete`, {}); toast.success("Task completed"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="cm-tasks"><p className="mb-1 border-b rule-strong pb-1.5 text-[13px] font-semibold">Open tasks · {open.length}</p>
      {open.map((t) => (
        <div key={t.id} className="grid grid-cols-[1fr_auto] items-center gap-3 border-b rule py-2">
          <span className="text-[13.5px]"><span className="font-medium">{t.title}</span><span className={`block text-[12.5px] ${t.due_date < today ? "font-medium text-[color:var(--fail)]" : "text-[color:var(--mute)]"}`}>{t.due_date < today ? "Overdue" : "Due"} {fmt(t.due_date)}{t.due_time ? ` ${t.due_time}` : ""} · {t.assignee_name}</span></span>
          <button className="btn btn-line !px-3 !py-1.5 !text-[13px]" disabled={pending} onClick={() => done(t)} data-testid={`cm-task-done-${t.id}`}>Complete</button>
        </div>
      ))}
    </div>
  );
};


export const Actions = ({ d, pid, run, keys }) => {
  const { primary, others } = actionsFor(d);
  return (
    <div data-testid="cm-actions">
      {primary && (
        <button onClick={() => run(pid, primary)} className="btn btn-primary w-full justify-between !py-3.5 !text-[15px]" data-testid="cm-primary-action">
          <span className="flex flex-col items-start"><span className="text-[11.5px] font-medium opacity-80">Recommended next action</span>{primary.label}</span><Kbd>{keys ? "A" : "↵"}</Kbd>
        </button>
      )}
      <ul className="mt-2">
        {others.map((a, i) => (
          <li key={a.key}>
            <button disabled={!a.enabled} onClick={() => run(pid, a)} data-testid={`cm-action-${a.key}`}
              className="grid w-full grid-cols-[1fr_auto] items-center gap-3 border-b rule px-2 py-2.5 text-left text-[14px] enabled:hover:bg-[color:var(--surface)] disabled:cursor-not-allowed">
              <span className={a.enabled ? "font-medium" : "text-[color:var(--mute)]"}>{a.label}
                {!a.enabled && <span className="block text-[12.5px] font-normal leading-snug text-[color:var(--mute)]">Not available · {a.reason}</span>}</span>
              {keys ? <Kbd dim={!a.enabled}>{i + 1}</Kbd> : a.enabled && <ArrowRight size={14} className="text-[color:var(--mute)]" aria-hidden />}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Evidence = ({ ev, cols = 1 }) => (
  <div className={`grid gap-x-8 gap-y-6 ${cols === 3 ? "xl:grid-cols-3" : ""}`} data-testid="cm-evidence">
    {KINDS.map(([k, t]) => {
      const list = ev.checks.filter((c) => c.kind === k);
      if (!list.length) return null;
      const ok = list.filter((c) => c.outcome === "supported").length;
      return (
        <div key={k}>
          <p className="mb-1 flex items-baseline justify-between border-b rule-strong pb-1.5 text-[13px] font-semibold">{t}<span className="font-medium text-[color:var(--mute)] tnum">{ok} of {list.length} {k === "exclusion" ? "clear" : "met"}</span></p>
          {list.map((c) => (
            <div key={c.key + k} className="grid grid-cols-[16px_1fr] gap-2.5 border-b rule py-2">
              <span className="pt-[3px]"><Mark outcome={c.outcome} kind={k} label={c.short || c.label} /></span>
              <span className="text-[13.5px] leading-snug"><span className="font-medium">{c.short || c.label}</span>
                <span className={`ml-2 text-[12.5px] font-semibold ${OUT_TONE[c.outcome]}`}>{outcomeText(k, c.outcome)}</span>
                <span className="block text-[13px] text-[color:var(--mute)]">{c.detail}</span></span>
            </div>
          ))}
        </div>
      );
    })}
  </div>
);

const Relationships = ({ d, over }) => {
  const list = [...(d.relationship ? [{ ...d.relationship, study_ref: d.study.reference, current: true, status: over || d.relationship.status }] : []), ...d.other_relationships];
  if (!list.length) return <p className="text-[13.5px] text-[color:var(--mute)]">Not in any study yet.</p>;
  return list.map((l) => (
    <div key={l.id} className="grid grid-cols-[104px_1fr_auto] items-center gap-3 border-b rule py-2.5" data-testid="cm-relationship">
      <span className={`mono ${l.current ? "font-semibold" : "text-[color:var(--ink-2)]"}`}>{l.study_ref}</span><StatusMeter status={l.status} />
      <span className="text-[12.5px] text-[color:var(--mute)]">{l.current ? "This study" : `Since ${fmt(l.added_at)}`}</span>
    </div>
  ));
};

const Assessment = ({ d, status, big }) => (
  <div className={`flex flex-wrap items-center gap-x-5 gap-y-2 ${big ? "rounded-[8px] bg-[color:var(--surface)] px-5 py-4" : ""}`} data-testid="cm-assessment">
    <Group group={d.evaluation.group} size={big ? 22 : 16} /><span className="h-5 w-px bg-[color:var(--rule-strong)]" /><Ready r={d.evaluation.readiness} />
    <span className="text-[13.5px] text-[color:var(--mute)]">{status ? `${STATUS_LABEL[status]} in this study` : "Not yet in this study"}</span>
  </div>
);

const Inspector = ({ studyId, pid, host }) => {
  const { data: d } = useCandidate(studyId, pid);
  if (!d) return <p className="p-7 text-[color:var(--mute)]">Loading…</p>;
  const p = d.participant;
  return (
    <div className="cm-in space-y-6 px-7 py-6" key={pid} data-testid="cm-inspector">
      <div>
        <p className="mono text-[color:var(--mute)]">{p.participant_ref} · {p.age ?? "age not recorded"} · {p.sex || "sex not recorded"} · {p.area}</p>
        <div className="mt-1 flex items-baseline justify-between gap-3"><h2 className="text-[28px] font-semibold leading-tight tracking-[-0.035em]" data-testid="cm-inspector-name">{p.first_name} {p.last_name}</h2>
          <Link to={to("participant", `?p=${pid}&study=${studyId}`)} className="whitespace-nowrap text-[13px] font-medium text-[color:var(--accent-text)] hover:underline" data-testid="cm-open-360">Participant 360 →</Link></div>
      </div>
      <Assessment d={d} status={d.relationship?.status} />
      {d.run_group && d.run_group !== d.evaluation.group && <p className="text-[12.5px] text-[color:var(--mute)]">The last match run grouped this candidate as {GROUP_LABEL[d.run_group]}. Current data gives {GROUP_LABEL[d.evaluation.group]}.</p>}
      <Restrictions d={d} />
      <ToConfirm d={d} host={host} />
      <ActionList d={d} host={host} />
      <Tasks d={d} />
      <Evidence ev={d.evaluation} />
      <div><p className="mb-1 border-b rule-strong pb-1.5 text-[13px] font-semibold">Study relationships · independent per study</p><Relationships d={d} /></div>
    </div>
  );
};

const NoRun = () => (
  <div className="mt-8 max-w-[640px] rounded-[8px] border border-dashed rule-strong px-6 py-8" data-testid="cm-no-run">
    <p className="h2">No candidates yet</p>
    <p className="mt-2 text-[14.5px] text-[color:var(--ink-2)]">This study has no match run yet, so there are no ranked candidates to review. Run matching from the working demonstrator. The prototype only reads data and never starts a run.</p>
  </div>
);

const TEMPLATE = (n) => `40px minmax(150px,1.3fr) 122px ${n * 19 + 14}px 160px 118px minmax(0,1fr)`;

const Header = ({ w, mode }) => {
  const s = w.wb.study;
  return (
    <div className="flex flex-wrap items-end justify-between gap-6">
      <div className="min-w-0">
        <p className="eyebrow"><span className="mono">{s.reference}</span> · <span className="capitalize">{s.therapeutic_area.replace(/_/g, " ")}</span> · {s.phase}{w.wb.run ? <> · matched {fmt(w.wb.run.run_at)} · {w.wb.run.pool_size} in pool of {w.wb.run.database_size?.toLocaleString()}</> : " · no match run yet"}</p>
        <h1 className="mt-1.5 truncate text-[30px] font-semibold leading-tight tracking-[-0.035em]" data-testid="cm-study-name">{s.name}</h1>
      </div>
      <div className="flex items-center gap-6">
        <div className="w-[190px]" data-testid="cm-study-target">
          <p className="flex items-baseline justify-between"><span className="text-[24px] font-semibold tracking-[-0.04em] tnum">{w.wb.pipeline.ready_for_first_visit}<span className="text-[14px] font-medium text-[color:var(--mute)]"> / {s.target}</span></span><span className="text-[12.5px] text-[color:var(--mute)]">ready for visit</span></p>
          <div className="mt-1 h-[4px] rounded-full bg-[color:var(--surface-2)]"><div className="h-full rounded-full bg-[color:var(--accent)]" style={{ width: `${(w.wb.pipeline.ready_for_first_visit / s.target) * 100}%` }} /></div>
        </div>
        <Link to={`/studies/${w.studyId}/edit`} className="btn btn-line !py-[7px]" data-testid="cm-edit-criteria">Edit criteria</Link>
        <ModeSwitch mode={mode} studyId={w.studyId} />
      </div>
    </div>
  );
};

export const ClinicalWorkbench = () => {
  const w = useWB("all");
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState(false);
  const sel = useSelection(w.rows);
  const box = useRef(null);
  const search = useRef(null);
  const v = useVirtual(w.rows.length, 54, box);
  const { data: cand } = useCandidate(w.studyId, sel.pid);
  const host = useActionHost(cand?.participant.id === sel.pid ? cand : undefined, w.studyId);
  const { data: first } = useCandidate(w.studyId, w.wb?.rows[0]?.participant_id);
  const step = (n) => v.reveal(sel.move(n));
  useEffect(() => { v.reveal(sel.idx); }, [sel.idx, w.wb]); // eslint-disable-line react-hooks/exhaustive-deps
  useHotkeys({ "mod+k": () => setOpen(true), j: () => step(1), k: () => step(-1), arrowdown: () => step(1), arrowup: () => step(-1), "/": () => search.current?.focus(), enter: () => cand?.participant.id === sel.pid && host.run(eActions(cand).primary), t: () => nav(to("focus", `?study=${w.studyId}`)) });
  if (!w.wb) return <Shell active="workbench" fixed onSearch={() => setOpen(true)}><p className="p-10 text-[color:var(--mute)]">Loading…</p></Shell>;
  if (!w.wb.rows.length) return <Shell active="workbench" fixed onSearch={() => setOpen(true)}><div className="px-8 pt-6"><Header w={w} mode="workbench" /><NoRun /></div><Palette open={open} setOpen={setOpen} studies={w.studies || []} studyId={w.studyId} /></Shell>;
  const nReq = reqCount(w.wb.study.criteria), n = w.wb.rows[0]?.evidence.length || 0;
  return (
    <Shell active="workbench" fixed onSearch={() => setOpen(true)}>
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] 2xl:grid-cols-[minmax(0,1fr)_440px]">
        <div className="flex min-h-0 flex-col">
          <div className="space-y-4 px-8 pb-4 pt-6">
            <Header w={w} mode="workbench" />
            <CriteriaStrip checks={first?.evaluation.checks} />
            <Distribution rows={w.wb.rows} group={w.group} setGroup={w.setGroup} />
            <div className="flex flex-wrap items-center gap-3">
              <label className="relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--mute)]" aria-hidden />
                <input ref={search} value={w.q} onChange={(e) => w.setQ(e.target.value)} placeholder="Filter by name or ID" aria-label="Filter candidates" className="field w-[260px] !py-[7px] !pl-9" data-testid="cm-filter" /></label>
              <div className="seg" role="group" aria-label="Contact readiness">
                {[["all", "Any contact"], ["ready", "Ready"], ["restricted", "Restricted"], ["blocked", "Blocked"]].map(([r, l]) => <button key={r} onClick={() => w.setReady(r)} aria-pressed={w.ready === r} data-testid={`cm-ready-${r}`}>{l}</button>)}
              </div>
              <span className="ml-auto text-[13px] text-[color:var(--mute)]" data-testid="cm-count">{w.rows.length} candidates · ranked by match, then readiness</span>
              <button className="btn btn-line !py-[7px]" disabled={!w.rows.length} onClick={() => setBulk(true)} data-testid="cm-bulk-queue">Bulk Queue…</button>
            </div>
          </div>
          <div className="mx-8 grid items-end gap-0 border-b rule-strong pb-2 text-[12.5px] font-semibold text-[color:var(--mute)]" style={{ gridTemplateColumns: TEMPLATE(n) }}>
            {["Rank", "Candidate", "Match", "Evidence", "Contact", "Status", "Next action"].map((h) => <span key={h} className="px-1">{h}</span>)}
          </div>
          <div ref={box} className="scroll min-h-0 flex-1 overflow-auto px-8" data-testid="cm-table" role="listbox" aria-label="Candidates">
            {!w.rows.length && <p className="py-10 text-[15px] text-[color:var(--mute)]">No candidates match these filters.</p>}
            <div style={{ height: v.total, position: "relative" }}>
              {w.rows.slice(v.start, v.end).map((r, i) => {
                const on = r.participant_id === sel.pid, st = r.status;
                return (
                  <button key={r.participant_id} onClick={() => sel.select(r.participant_id)} role="option" aria-selected={on} data-testid={`cm-row-${r.participant_ref}`}
                    className={`absolute left-0 right-0 grid h-[54px] items-center border-b rule text-left ${on ? "row-sel" : "row-hov"}`} style={{ top: v.top + i * 54, gridTemplateColumns: TEMPLATE(n) }}>
                    <span className="mono tnum px-2 text-[color:var(--mute)]">{r.rank}</span>
                    <span className="min-w-0 px-1"><span className="block truncate text-[14.5px] font-medium">{r.name}</span><span className="mono text-[12px] text-[color:var(--mute)]">{r.participant_ref} · {r.age ?? "age ?"}{r.other_studies.length > 0 && <span className="ml-2 font-sans text-[12px] font-medium text-[color:var(--conflict)]">+{r.other_studies.length} other study</span>}</span></span>
                    <span className="px-1"><Group group={r.group} size={13.5} /></span>
                    <span className="flex items-center gap-[5px] px-1">{rowEvidence(r, nReq).map((e, ci) => <span key={ci} className={ci === nReq ? "ml-[5px] border-l rule-strong pl-[7px]" : ""}><Mark outcome={e.outcome} kind={e.kind} label={e.label} /></span>)}</span>
                    <span className="min-w-0 px-1"><Ready r={r.readiness} /><span className="block truncate text-[12.5px] text-[color:var(--mute)]">{r.permission}{r.restrictions.length > 0 && ` · ${r.restrictions.length} restriction${r.restrictions.length > 1 ? "s" : ""}`}</span></span>
                    <span className="px-1"><StatusMeter status={st} /></span>
                    <span className="truncate px-1 pr-3 text-[13.5px] text-[color:var(--ink-2)]">{r.next_action}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t rule px-8 py-2.5"><Legend /><span className="text-[12px] text-[color:var(--mute)]"><Kbd>J</Kbd> <Kbd>K</Kbd> move · <Kbd>↵</Kbd> primary · <Kbd>/</Kbd> filter · <Kbd>T</Kbd> focus</span></div>
        </div>
        <aside className="scroll min-h-0 overflow-auto border-l rule bg-[color:var(--raise)]" aria-label="Selected candidate">{sel.pid && w.rows.length > 0 ? <Inspector studyId={w.studyId} pid={sel.pid} host={host} /> : <p className="px-7 py-8 text-[14px] text-[color:var(--mute)]" data-testid="cm-inspector-empty">No candidate selected. Change the filters to see candidates.</p>}</aside>
      </div>
      {host.dialogs}
      <BulkQueue open={bulk} close={() => setBulk(false)} studyId={w.studyId} ids={w.rows.map((r) => r.participant_id)} label={`${w.group === "all" ? "all groups" : GROUP_LABEL[w.group]}, ${w.ready === "all" ? "any contact status" : w.ready}${w.q ? `, “${w.q}”` : ""} (${w.rows.length})`} />
      <Palette open={open} setOpen={setOpen} rows={w.wb.rows} studies={w.studies || []} studyId={w.studyId} />
    </Shell>
  );
};

export const ClinicalFocus = () => {
  const w = useWB("strong");
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState({});
  const [log, setLog] = useState([]);
  const sel = useSelection(w.rows);
  const box = useRef(null);
  const v = useVirtual(w.rows.length, 64, box);
  const { data: d0 } = useCandidate(w.studyId, sel.pid);
  const d = d0?.participant.id === sel.pid ? d0 : undefined;
  const host = useActionHost(d, w.studyId);
  const step = (n) => v.reveal(sel.move(n));
  useEffect(() => { v.reveal(sel.idx); }, [sel.idx, w.wb]); // eslint-disable-line react-hooks/exhaustive-deps
  const fresh = d && d.participant.id === w.rows[sel.ref.current]?.participant_id;
  const decide = async (a, label) => {
    const r = w.rows[sel.ref.current];
    if (!r || (a && !fresh) || host.pending) return;
    if (a && !(await host.run(a))) return;
    setDone((x) => ({ ...x, [r.participant_id]: label || a?.label }));
    setLog((l) => [{ id: `${r.participant_id}-${l.length}-${Date.now()}`, who: r.name, what: label || a?.label, at: new Date() }, ...l].slice(0, 8));
    step(1);
  };
  const { primary, others } = eActions(d);
  useHotkeys({
    "mod+k": () => setOpen(true), j: () => step(1), k: () => step(-1), arrowdown: () => step(1), arrowup: () => step(-1),
    a: () => primary && decide(primary), x: () => decide(null, "Skipped"), t: () => nav(to("workbench", `?study=${w.studyId}&p=${sel.pid}`)),
    ...Object.fromEntries(others.map((a, i) => [String(i + 1), () => a.enabled && decide(a)])),
  });
  if (!w.wb) return <Shell active="focus" fixed onSearch={() => setOpen(true)}><p className="p-10 text-[color:var(--mute)]">Loading…</p></Shell>;
  if (!w.wb.rows.length) return <Shell active="focus" fixed onSearch={() => setOpen(true)}><div className="px-8 pt-5"><Header w={w} mode="focus" /><NoRun /></div><Palette open={open} setOpen={setOpen} studies={w.studies || []} studyId={w.studyId} /></Shell>;
  const nReq = reqCount(w.wb.study.criteria);
  const reviewed = w.rows.filter((r) => done[r.participant_id]).length;
  const p = d?.participant;
  return (
    <Shell active="focus" fixed onSearch={() => setOpen(true)}>
      <div className="space-y-4 border-b rule px-8 pb-4 pt-5">
        <Header w={w} mode="focus" />
        <Distribution rows={w.wb.rows} group={w.group} setGroup={w.setGroup} compact />
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)_360px]">
        <div className="flex min-h-0 flex-col border-r rule">
          <div className="border-b rule px-5 py-3" data-testid="cm-progress">
            <p className="flex items-baseline justify-between text-[13px] font-medium"><span>Reviewed this session</span><span className="text-[18px] font-semibold tracking-[-0.03em] tnum">{reviewed}<span className="text-[13px] text-[color:var(--mute)]"> / {w.rows.length}</span></span></p>
            <div className="mt-1.5 h-[4px] rounded-full bg-[color:var(--surface-2)]"><div className="h-full rounded-full bg-[color:var(--accent)] transition-[width]" style={{ width: `${w.rows.length ? (reviewed / w.rows.length) * 100 : 0}%` }} /></div>
          </div>
          <div ref={box} className="scroll min-h-0 flex-1 overflow-auto" data-testid="cm-focus-queue" role="listbox" aria-label="Review queue">
            <div style={{ height: v.total, position: "relative" }}>
              {w.rows.slice(v.start, v.end).map((r, i) => {
                const on = r.participant_id === sel.pid, dn = done[r.participant_id];
                return (
                  <button key={r.participant_id} onClick={() => sel.select(r.participant_id)} role="option" aria-selected={on} data-testid={`cm-fq-${r.participant_ref}`}
                    className={`absolute left-0 right-0 flex h-[64px] flex-col justify-center gap-1.5 border-b rule px-5 text-left ${on ? "row-sel" : "row-hov"} ${dn && !on ? "opacity-55" : ""}`} style={{ top: v.top + i * 64 }}>
                    <span className="flex items-baseline justify-between gap-2 text-[14px] font-medium"><span className="truncate">{r.name}</span><span className="mono text-[11.5px] text-[color:var(--mute)]">#{r.rank}</span></span>
                    <span className="flex items-center gap-[4px]">{rowEvidence(r, nReq).map((e, ci) => <Mark key={ci} outcome={e.outcome} kind={e.kind} label={e.label} size={11} />)}
                      <span className={`ml-auto text-[12px] font-medium ${dn ? "text-[color:var(--accent-text)]" : "text-[color:var(--mute)]"}`}>{dn ? `✓ ${dn}` : r.readiness === "ready" ? "Ready" : r.readiness === "blocked" ? "Blocked" : "Restricted"}</span></span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <main className="scroll min-h-0 overflow-auto px-12 py-8" data-testid="cm-focus-sheet">
          {!w.rows.length && <p className="text-[18px] text-[color:var(--mute)]">No candidates in this group.</p>}
          {d && (
            <div className="cm-in mx-auto max-w-[920px] space-y-7" key={sel.pid}>
              <div>
                <p className="eyebrow">Candidate {sel.idx + 1} of {w.rows.length}</p>
                <h2 className="display mt-2 text-[56px]" data-testid="cm-focus-name">{p.first_name} {p.last_name}</h2>
                <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-[14px]">
                  {[["ID", <span className="mono">{p.participant_ref}</span>], ["Age", p.age ?? "Not recorded"], ["Sex", p.sex || "Not recorded"], ["Area", p.area], ["Last contact", rel(p.last_contact_at)], ["Consent", w.rows[sel.idx]?.permission || "—"]].map(([k, val]) => (
                    <div key={k}><dt className="text-[12.5px] text-[color:var(--mute)]">{k}</dt><dd className={`font-medium ${k === "Sex" ? "capitalize" : ""}`}>{val}</dd></div>
                  ))}
                </dl>
              </div>
              <Assessment d={d} status={d.relationship?.status} big />
              <Restrictions d={d} />
              <ToConfirm d={d} host={host} />
              <Tasks d={d} />
              <Evidence ev={d.evaluation} cols={3} />
              <Link to={to("participant", `?p=${sel.pid}&study=${w.studyId}`)} className="inline-flex text-[13.5px] font-medium text-[color:var(--accent-text)] hover:underline">Open Participant 360 →</Link>
            </div>
          )}
        </main>
        <aside className="scroll flex min-h-0 flex-col gap-6 overflow-auto border-l rule bg-[color:var(--raise)] px-6 py-6" aria-label="Decision">
          <div>
            <p className="mb-3 text-[13px] font-semibold">Decide</p>
            {d && <ActionList d={d} host={{ ...host, run: (a) => decide(a) }} keys />}
            <button onClick={() => decide(null, "Skipped")} className="btn btn-line mt-3 w-full justify-between" data-testid="cm-skip">Skip for now<Kbd>X</Kbd></button>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12.5px] text-[color:var(--mute)]">
            {[["J / K", "Next / previous"], ["A", "Recommended action"], ["1–6", "Alternatives"], ["T", "Overview table"], ["⌘K", "Search"]].map(([k, l]) => <span key={k} className="contents"><span><Kbd>{k}</Kbd></span><span>{l}</span></span>)}
          </div>
          <div data-testid="cm-session-log">
            <p className="mb-1 border-b rule-strong pb-1.5 text-[13px] font-semibold">This session</p>
            {log.length ? log.map((l) => <p key={l.id} className="flex justify-between gap-2 border-b rule py-2 text-[13px]"><span><span className="font-medium">{l.who}</span> · {l.what}</span><span className="mono text-[11.5px] text-[color:var(--mute)]">{l.at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span></p>)
              : <p className="py-2 text-[13px] text-[color:var(--mute)]">No decisions yet. Direct actions move to the next candidate. Actions that open a form wait until you save.</p>}
          </div>
        </aside>
      </div>
      {host.dialogs}
      <Palette open={open} setOpen={setOpen} rows={w.wb.rows} studies={w.studies || []} studyId={w.studyId} />
    </Shell>
  );
};
